import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * Three-code language selector (EN | FR | DE).
 * `transparent` toggles the inactive-colour set for dark-over-hero headers.
 */
export default function LanguageSwitcher({ transparent = false, testIdPrefix = 'lang' }) {
  const { i18n, t } = useTranslation();
  const current = (i18n.language || 'en').slice(0, 2);

  const activeClass = 'text-brand-accent font-semibold';
  const inactiveClass = transparent
    ? 'text-white/60 hover:text-white/90 font-medium'
    : 'text-black/50 hover:text-brand-primary font-medium';
  const sepClass = transparent ? 'text-white/30' : 'text-black/20';

  const handleChange = (code) => {
    if (code === current) return;
    i18n.changeLanguage(code);
    try {
      localStorage.setItem('i18nextLng', code);
    } catch {
      /* noop */
    }
  };

  return (
    <div
      className="inline-flex items-center text-[13px] uppercase"
      style={{ letterSpacing: '0.14em' }}
      role="group"
      aria-label={t('language.label')}
      data-testid={`${testIdPrefix}-switcher`}
    >
      {SUPPORTED_LANGUAGES.map((code, idx) => (
        <span key={code} className="inline-flex items-center">
          {idx > 0 && <span className={cn('mx-2.5 select-none', sepClass)} aria-hidden="true">|</span>}
          <button
            type="button"
            onClick={() => handleChange(code)}
            className={cn(
              'bg-transparent p-0 border-0 cursor-pointer transition-colors duration-150',
              code === current ? activeClass : inactiveClass
            )}
            aria-pressed={code === current}
            aria-label={t(`language.${code}`)}
            data-testid={`${testIdPrefix}-${code}`}
          >
            {t(`language.${code}`)}
          </button>
        </span>
      ))}
    </div>
  );
}
